import React, { useState } from "react";
import {
  Paper,
  TextField,
  Typography,
  FormControlLabel,
  Switch,
  MenuItem,
  Box,
  Checkbox,
  Button,
  IconButton,
  FormControl,
} from "@mui/material";

import { styled } from "@mui/material/styles";
import WarningAmberIcon from "@mui/icons-material/WarningAmber";
import PersonIcon from "@mui/icons-material/Person";
import LocationOnIcon from "@mui/icons-material/LocationOn";
import HomeIcon from "@mui/icons-material/Home";
import LocalShippingIcon from "@mui/icons-material/LocalShipping";
import NotesIcon from "@mui/icons-material/Notes";
import { Trash2 } from "lucide-react";
import UploadFileIcon from "@mui/icons-material/UploadFile";
import DescriptionIcon from "@mui/icons-material/Description";
import PictureAsPdfIcon from "@mui/icons-material/PictureAsPdf";
import GridOnIcon from "@mui/icons-material/GridOn"; // Excel files
import InsertDriveFileIcon from "@mui/icons-material/InsertDriveFile"; // generic

// -------- Reuse styled components from Pickup (copy-pasted) -------- //
const StyledPaper = styled(Paper)(({ theme }) => ({
  background: "linear-gradient(135deg, #ffffff 0%, #f8fafb 100%)",
  borderRadius: "20px",
  border: "1px solid rgba(0, 0, 0, 0.06)",
  boxShadow: "0 10px 40px rgba(0, 0, 0, 0.08)",
  padding: "48px",
  maxWidth: "1200px",
  margin: "0 auto",
  transition: "all 0.3s cubic-bezier(0.4, 0, 0.2, 1)",
  "&:hover": {
    boxShadow: "0 15px 50px rgba(0, 0, 0, 0.12)",
  },
  [theme.breakpoints.down("md")]: {
    padding: "24px",
  },
}));

const StyledTextField = styled(TextField)(({ theme }) => ({
  "& .MuiOutlinedInput-root": {
    borderRadius: "12px",
    backgroundColor: "#ffffff",
    transition: "all 0.3s cubic-bezier(0.4, 0, 0.2, 1)",
    border: "2px solid rgba(0, 0, 0, 0.06)",
    fontSize: "15px",
    fontWeight: "500",
    color: "#1a1a1a",
    "& fieldset": {
      borderColor: "rgba(0, 0, 0, 0.06)",
    },
    "&:hover fieldset": {
      borderColor: "rgba(25, 118, 210, 0.3)",
    },
    "&.Mui-focused fieldset": {
      borderColor: "primary.main",
      borderWidth: "2px",
    },
    "&.Mui-focused": {
      boxShadow: "0 0 0 3px rgba(25, 118, 210, 0.1)",
      backgroundColor: "#ffffff",
    },
  },
  "& .MuiInputBase-input": {
    padding: "16px 16px",
    fontFamily: "'Inter', sans-serif",
    fontWeight: "500",
    "&::placeholder": {
      opacity: "0.6",
    },
  },
  "& .MuiInputLabel-root": {
    fontFamily: "'Inter', sans-serif",
    fontWeight: "600",
    color: "#4a5568",
    fontSize: "14px",
    transform: "translate(14px, -12px) scale(0.75)",
    backgroundColor: "#ffffff",
    paddingLeft: "6px",
    paddingRight: "6px",
    marginLeft: "-3px",
    "&.Mui-focused": {
      color: "primary.main",
    },
  },
  "& .MuiFormHelperText-root": {
    fontFamily: "'Inter', sans-serif",
    fontSize: "13px",
    marginLeft: "0",
    color: "#718096",
    marginTop: "8px",
  },
}));

const SectionTitle = styled(Box)({
  display: "flex",
  alignItems: "center",
  gap: "12px",
  marginBottom: "24px",
  marginTop: "32px",
  paddingTop: "24px",
  borderTop: "2px solid rgba(0, 0, 0, 0.05)",
  "&:first-of-type": {
    marginTop: 0,
    paddingTop: 0,
    borderTop: "none",
  },
});

const IconWrapper = styled(Box)({
  width: "28px",
  height: "28px",
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  borderRadius: "8px",
  backgroundColor: "rgba(25,118,210,0.1)",
  "& svg": {
    width: "16px",
    height: "16px",
  },
});

const SectionLabel = styled(Typography)({
  fontWeight: "700",
  textTransform: "uppercase",
  letterSpacing: "1px",
  color: "primary.main",
  fontSize: "14px",
});

const HeadingBox = styled(Box)({
  marginBottom: "40px",
  paddingBottom: "32px",
  borderBottom: "2px solid rgba(0, 0, 0, 0.05)",
});

const FormGrid = styled(Box)(({ theme }) => ({
  display: "grid",
  gridTemplateColumns: "repeat(4, 1fr)",
  gap: "28px",
  marginBottom: "8px",
  [theme.breakpoints.down("md")]: {
    gridTemplateColumns: "1fr",
  },
}));

const FullWidthGrid = styled(Box)(({ theme }) => ({
  display: "grid",
  gridTemplateColumns: "repeat(2, 1fr)",
  gap: "28px",
  marginBottom: "8px",
  [theme.breakpoints.down("md")]: {
    gridTemplateColumns: "1fr",
    gap: "20px",
  },
}));

const FourColGrid = styled(Box)(({ theme }) => ({
  display: "grid",
  gridTemplateColumns: "repeat(4, 1fr)",
  gap: "28px",
  marginBottom: "20px",
  [theme.breakpoints.down("lg")]: {
    gridTemplateColumns: "repeat(2, 1fr)",
  },
  [theme.breakpoints.down("sm")]: {
    gridTemplateColumns: "1fr",
  },
}));

const AssistanceBox = styled(Box)(({ theme }) => ({
  padding: "20px",
  borderRadius: "12px",
  background:
    "linear-gradient(135deg, rgba(25, 118, 210, 0.04) 0%, rgba(25, 118, 210, 0.02) 100%)",
  border: "1px solid rgba(25, 118, 210, 0.1)",
  marginBottom: "24px",
  marginTop: "24px",
  display: "flex",
  // alignItems: "center",
  flexDirection: "column",
  transition: "all 0.3s cubic-bezier(0.4, 0, 0.2, 1)",
  "&:hover": {
    borderColor: "rgba(25, 118, 210, 0.2)",
    backgroundColor: "rgba(25, 118, 210, 0.06)",
  },
}));

const NotesBox = styled(Box)({
  marginBottom: "8px",
});


// -------------------------------------------------------------------- //

export default function DeliveryAddressForm({
  delivery,
  onChange,
  onToggle,
  errors,
}) {
  const locationTypes = ["None", "Warehouse", "Factory", "Home", "Yard"];
  const equipmentTypes = ["None", "Forklift", "Crane", "Manual"];
  const timeSlots = [
    "None",
    "9:00 AM - 12:00 PM",
    "12:00 PM - 3:00 PM",
    "3:00 PM - 6:00 PM",
    "6:00 PM - 9:00 PM",
  ];
  const [localPickup, setLocalPickup] = useState({
    delivery,
  });

  const handleFieldChange = (field) => (e) => onChange(field)(e.target.value);

  const handleSelectChange = (field) => (e) => onChange(field)(e.target.value);

  const handleToggleChange = (field) => (e) => {
    if (onToggle) onToggle(field)(e);
  };

  const handleFileUpload = (type) => (e) => {
    const files = Array.from(e.target.files);
    const current = delivery[type] || [];

    // Limit to 5 files
    const combined = [...current, ...files].slice(0, 5);

    onChange(type)(combined); // Update parent state
  };

  const handleDeleteFile = (type, index) => {
    const updatedFiles = delivery[type].filter((_, i) => i !== index);
    onChange(type)(updatedFiles); // Update parent state
  };

  return (
    <StyledPaper elevation={0}>
      {/* HEADER */}
      <HeadingBox>
        <Typography
          variant="h4"
          sx={{
            fontWeight: "800",
            fontSize: "30px",
            background: "linear-gradient(135deg, #0a0e27 0%, #1976d2 100%)",
            WebkitBackgroundClip: "text",
            WebkitTextFillColor: "transparent",
            marginBottom: "12px",
          }}
        >
          Delivery Address
        </Typography>

        <Typography
          sx={{ color: "#718096", fontSize: "15px", fontWeight: 500 }}
        >
          Provide delivery location details for drop-off
        </Typography>
      </HeadingBox>

      {/* CONTACT */}
      <SectionTitle>
        <IconWrapper>
          <PersonIcon />
        </IconWrapper>
        <SectionLabel>Contact Information</SectionLabel>
      </SectionTitle>

      <FormGrid>
        <StyledTextField
          fullWidth
          label="First Name *"
          value={delivery.firstName}
          onChange={handleFieldChange("firstName")}
          error={!!errors?.firstName}
          helperText={errors?.firstName}
          variant="outlined"
        />
        <StyledTextField
          fullWidth
          label="Last Name *"
          value={delivery.lastName}
          onChange={handleFieldChange("lastName")}
          error={!!errors?.lastName}
          helperText={errors?.lastName}
          variant="outlined"
        />
        <StyledTextField
          fullWidth
          label="Contact Phone Number *"
          value={delivery.phone}
          onChange={handleFieldChange("phone")}
          error={!!errors?.phone}
          helperText={errors?.phone}
          variant="outlined"
        />
        <StyledTextField
          fullWidth
          label="Company Name *"
          value={delivery.companyName}
          onChange={handleFieldChange("companyName")}
          error={!!errors?.companyName}
          helperText={errors?.companyName}
          variant="outlined"
        />

        <StyledTextField
          fullWidth
          label="ABN/ACN/VAT/Tax ID *"
          value={delivery.vat}
          onChange={handleFieldChange("vat")}
          error={!!errors?.vat}
          helperText={errors?.vat}
          variant="outlined"
        />
      </FormGrid>

      {/* LOCATION */}
      <SectionTitle>
        <IconWrapper>
          <LocationOnIcon />
        </IconWrapper>
        <SectionLabel>Location Details</SectionLabel>
      </SectionTitle>

      <FormGrid>
        <StyledTextField
          fullWidth
          label="Country *"
          value={delivery.country}
          onChange={handleFieldChange("country")}
          error={!!errors?.country}
          helperText={errors?.country}
        />

        <StyledTextField
          fullWidth
          label="State / Province *"
          value={delivery.state}
          onChange={handleFieldChange("state")}
          error={!!errors?.state}
          helperText={errors?.state}
        />

        <StyledTextField
          fullWidth
          label="City *"
          value={delivery.city}
          onChange={handleFieldChange("city")}
          error={!!errors?.city}
          helperText={errors?.city}
        />

        <StyledTextField
          fullWidth
          label="Postcode *"
          value={delivery.postcode}
          onChange={handleFieldChange("postcode")}
          error={!!errors?.postcode}
          helperText={errors?.postcode}
        />
      </FormGrid>

      {/* ADDRESS */}
      <SectionTitle>
        <IconWrapper>
          <HomeIcon />
        </IconWrapper>
        <SectionLabel>Address</SectionLabel>
      </SectionTitle>

      <FullWidthGrid>
        <StyledTextField
          fullWidth
          label="Address Line 1 *"
          value={delivery.addressLine1}
          onChange={handleFieldChange("addressLine1")}
          error={!!errors?.addressLine1}
          helperText={errors?.addressLine1}
        />

        <StyledTextField
          label="Address Line 2 (Optional)"
          value={delivery.addressLine2}
          onChange={handleFieldChange("addressLine2")}
          error={!!errors?.addressLine2}
          helperText={errors?.addressLine2}
        />
      </FullWidthGrid>

      {/* LOGISTICS */}
      <SectionTitle>
        <IconWrapper>
          <LocalShippingIcon />
        </IconWrapper>
        <SectionLabel>Logistics & Equipment</SectionLabel>
      </SectionTitle>

      <FormGrid>
        <StyledTextField
          fullWidth
          select
          label="Location Type *"
          value={delivery.locationType || "None"}
          onChange={handleSelectChange("locationType")}
          error={!!errors?.locationType}
          helperText={errors?.locationType}
        >
          {locationTypes.map((o) => (
            <MenuItem key={o} value={o}>
              {o}
            </MenuItem>
          ))}
        </StyledTextField>

        <FormControlLabel
          control={
            <Checkbox
              checked={delivery.forkliftAvailable}
              onChange={(e) => onToggle("forkliftAvailable")(e.target.checked)}
            />
          }
          label="Forklift Availability"
        />
      </FormGrid>

      {/* ASSISTANCE */}
      <AssistanceBox>
        <FormControlLabel
          control={
            <Switch
              checked={!!delivery.assistanceAvailable}
              onChange={handleToggleChange("assistanceAvailable")}
              sx={{
                "& .MuiSwitch-switchBase": {
                  color: "#cbd5e0",
                  "&.Mui-checked": {
                    color: "primary.main",
                    "& + .MuiSwitch-track": {
                      backgroundColor: "primary.main",
                    },
                  },
                },
                "& .MuiSwitch-track": {
                  backgroundColor: "#cbd5e0",
                },
              }}
            />
          }
          label={
            <Typography fontWeight={600}>
              {delivery.assistanceAvailable
                ? "Assistance Available at Delivery"
                : "No Assistance at Delivery"}
            </Typography>
          }
        />
        {delivery.assistanceAvailable && (
          <>
            <SectionTitle>
              <IconWrapper>
                <NotesIcon />
              </IconWrapper>
              <SectionLabel>Special Assistance Notes</SectionLabel>
            </SectionTitle>
            <StyledTextField
              fullWidth
              label="Special Assistance Notes (Optional)"
              multiline
              minRows={7}
              value={delivery.notes}
              onChange={handleFieldChange("specialNotes")}
              variant="outlined"
              helperText="Write any special instructions for driver..."
            />
          </>
        )}
      </AssistanceBox>

      {/* DELIVERY TIME SLOT */}
      <SectionTitle>
        <IconWrapper>
          <DescriptionIcon />
        </IconWrapper>
        <SectionLabel>Delivery Time Slot</SectionLabel>
      </SectionTitle>
      <FormGrid>
        <StyledTextField
          fullWidth
          type="date"
          label="Start Date"
          value={delivery.startDate}
          onChange={handleFieldChange("startDate")}
          error={!!errors?.startDate}
          helperText={errors?.startDate}
        />
        <StyledTextField
          fullWidth
          type="date"
          label="End Date"
          value={delivery.endDate}
          onChange={handleFieldChange("endDate")}
          error={!!errors?.endDate}
          helperText={errors?.endDate}
        />
        <StyledTextField
          fullWidth
          select
          label="Delivery Time Slot *"
          value={delivery.deliveryTimeSlot || "None"}
          onChange={handleSelectChange("deliveryTimeSlot")}
          error={!!errors?.timeSlot}
          helperText={errors?.timeSlot}
        >
          {timeSlots.map((o) => (
            <MenuItem key={o} value={o}>
              {o}
            </MenuItem>
          ))}
        </StyledTextField>
      </FormGrid>

      {/* NOTES */}
      <SectionTitle>
        <IconWrapper>
          <NotesIcon />
        </IconWrapper>
        <SectionLabel>Delivery Notes</SectionLabel>
      </SectionTitle>
      <NotesBox>
        <Box
          sx={{
            padding: "16px",
            borderRadius: "12px",
            backgroundColor: "rgba(255,193,7,0.08)",
            border: "1px solid rgba(255,193,7,0.3)",
            display: "flex",
            gap: "12px",
            alignItems: "flex-start",
            mb: 5,
            mt: 2,
          }}
        >
          {/* ICON BOX */}
          <Box
            sx={{
              width: "28px",
              height: "28px",
              borderRadius: "6px",
              backgroundColor: "rgba(255,193,7,0.25)",
              display: "flex",
              justifyContent: "center",
              alignItems: "center",
              mt: "2px",
            }}
          >
            <WarningAmberIcon sx={{ fontSize: 20, color: "#b7791f" }} />
          </Box>

          {/* TEXT CONTENT */}
          <Box sx={{ width: "100%" }}>
            <Typography
              sx={{
                fontWeight: 700,
                fontSize: "15px",
                mb: 1,
                color: "#8a6d3b",
              }}
            >
              Delivery Notes — What to include
            </Typography>

            {/* 2 COLUMN BULLET GRID */}
            <Box
              sx={{
                display: "grid",
                gridTemplateColumns: { xs: "1fr", sm: "1fr 1fr 1fr" },
                rowGap: "8px",
                columnGap: "16px",
              }}
            >
              <Typography sx={{ fontSize: "14px", color: "#6c757d" }}>
                • Unloading restrictions
              </Typography>

              <Typography sx={{ fontSize: "14px", color: "#6c757d" }}>
                • Location access details
              </Typography>

              <Typography sx={{ fontSize: "14px", color: "#6c757d" }}>
                • Preferred delivery timings
              </Typography>
            </Box>
          </Box>
        </Box>
        <StyledTextField
          fullWidth
          label="Delivery Notes (Important) *"
          multiline
          minRows={7}
          value={delivery.notes}
          onChange={handleFieldChange("deliveryNotes")}
          helperText= {errors?.notes ? "Include any important delivery instructions.":errors?.notes}
          error={!!errors?.notes}
          variant="outlined"
        />
      </NotesBox>

      {/* FILE UPLOADS */}
      <SectionTitle>
        <IconWrapper>
          <NotesIcon />
        </IconWrapper>
        <SectionLabel>Delivery Images/Documents</SectionLabel>
      </SectionTitle>

      <FullWidthGrid sx={{ display: "flex", flexDirection: "column", gap: 3 }}>
        {/* IMAGE UPLOAD SECTION */}
        <Box>
          <Button
            variant="outlined"
            component="label"
            startIcon={<UploadFileIcon />}
            disabled={delivery.images?.length >= 5}
            sx={{ mb: 1 }} // spacing under button
          >
            Upload Images ({delivery.images?.length || 0}/5)
            <input
              type="file"
              hidden
              multiple
              accept="image/*"
              onChange={handleFileUpload("images")}
            />
          </Button>

          {delivery.images?.length >= 5 && (
            <Typography color="error" variant="caption" sx={{ marginLeft: 2 }}>
              Maximum of 5 images reached.
            </Typography>
          )}

          {/* HORIZONTAL IMAGE PREVIEW */}
          <Box
            mt={2}
            sx={{
              display: "flex",
              gap: 5,
              overflowX: "auto",
              paddingBottom: 1,
              "&::-webkit-scrollbar": { height: 6 },
              "&::-webkit-scrollbar-thumb": {
                backgroundColor: "#ccc",
                borderRadius: 10,
              },
            }}
          >
            {delivery.images?.map((file, index) => (
              <Box
                key={index}
                sx={{
                  position: "relative",
                  minWidth: "180px",
                  height: "180px",
                  borderRadius: 2,
                  overflow: "hidden",
                  border: "1px solid #ddd",
                  flexShrink: 0,
                }}
              >
                <IconButton
                  size="small"
                  onClick={() => handleDeleteFile(index)}
                  sx={{
                    position: "absolute",
                    top: 6,
                    right: 6,
                    background: "rgba(0,0,0,0.6)",
                    color: "#fff",
                    "&:hover": {
                      background: "rgba(0,0,0,0.8)",
                    },
                  }}
                >
                  <Trash2 fontSize="small" />
                </IconButton>

                <img
                  src={URL.createObjectURL(file)}
                  alt={`preview-${index}`}
                  style={{
                    width: "100%",
                    height: "100%",
                    objectFit: "cover",
                  }}
                />
              </Box>
            ))}
          </Box>
        </Box>

        {/* DOCUMENT UPLOAD SECTION */}
        <Box>
          <Button
            variant="outlined"
            component="label"
            startIcon={<UploadFileIcon />}
            disabled={delivery.documents?.length >= 5}
            sx={{ mb: 1 }}
          >
            Upload Documents ({delivery.documents?.length || 0}/5)
            <input
              type="file"
              hidden
              multiple
              onChange={handleFileUpload("documents")}
            />
          </Button>

          {delivery.documents?.length >= 5 && (
            <Typography color="error" variant="caption" sx={{ marginLeft: 2 }}>
              Maximum of 5 documents reached.
            </Typography>
          )}

          {/* DOCUMENT LIST */}
          <Box mt={2} sx={{ display: "flex", gap: 2, overflowX: "auto" }}>
            {delivery.documents?.map((file, index) => {
              const ext = file.name.split(".").pop().toLowerCase();

              const getIcon = () => {
                if (ext === "pdf") return <PictureAsPdfIcon color="error" />;
                if (["doc", "docx"].includes(ext))
                  return <DescriptionIcon color="primary" />;
                if (["xls", "xlsx"].includes(ext))
                  return <GridOnIcon color="success" />;
                return <InsertDriveFileIcon />;
              };

              return (
                <Box
                  key={index}
                  sx={{
                    minWidth: "200px",
                    borderRadius: 2,
                    border: "1px solid #ddd",
                    padding: 2,
                    background: "#fafafa",
                    display: "flex",
                    justifyContent: "space-between",
                    alignItems: "center",
                  }}
                >
                  <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                    {getIcon()}
                    <Typography variant="body2" sx={{ fontWeight: 500 }}>
                      {file.name}
                    </Typography>
                  </Box>

                  <IconButton
                    size="small"
                    onClick={() => handleDeleteFile(index)}
                    sx={{ color: "red" }}
                  >
                    <Trash2 fontSize="small" />
                  </IconButton>
                </Box>
              );
            })}
          </Box>
        </Box>
      </FullWidthGrid>
    </StyledPaper>
  );
}
